import type { V2 } from '../core/math';
import type { CharacterSave, QuestProgress } from './character';

export type QuestKind = 'main' | 'side' | 'daily' | 'weekly' | 'world';

export type ObjectiveType = 'kill' | 'talk' | 'reach' | 'gather' | 'interact' | 'clear' | 'collect' | 'level' | 'flag' | 'use';

export interface QuestObjective {
  type: ObjectiveType;
  /** Monster def id, npc id, `zone:x,z`, item id, interactable kind or id, instance id, flag. */
  target: string;
  count: number;
  text: string;
  /** Zone where the objective happens (map/tracker markers). */
  zone?: string;
  pos?: V2;
  radius?: number;
}

export interface QuestStep {
  objectives: QuestObjective[];
  /** Line shown when the step starts (story beat / hint). */
  hint?: string;
}

export interface QuestRewards {
  xp: number;
  silver?: number;
  gold?: number;
  items?: { id: string; count: number }[];
  /** Level-appropriate gear piece of this rarity. */
  gear?: 'rare' | 'epic' | 'legendary';
  skillPoints?: number;
  /** Flags set on completion (story gates, unlocks). */
  flags?: string[];
  /** Legacy (bond) points with the turn-in NPC. */
  bond?: number;
  card?: string;
}

export interface QuestDef {
  id: string;
  kind: QuestKind;
  name: string;
  level: number;
  zone: string;
  giver: string;
  turnIn: string;
  requires?: string[];
  requiresLevel?: number;
  requiresFlag?: string;
  steps: QuestStep[];
  dialogue: { offer: string[]; progress?: string; complete: string[] };
  rewards: QuestRewards;
  /** Auto-accepted (main story continuations). */
  auto?: boolean;
}

export type QuestEvent =
  | { type: 'kill'; target: string }
  | { type: 'talk'; target: string }
  | { type: 'reach'; zone: string; pos: V2 }
  | { type: 'gather'; target: string; count?: number }
  | { type: 'interact'; target: string }
  | { type: 'clear'; target: string }
  | { type: 'collect'; target: string; count?: number }
  | { type: 'level'; level: number }
  | { type: 'flag'; target: string }
  | { type: 'use'; target: string };

const QUESTS = new Map<string, QuestDef>();

export function registerQuests(defs: QuestDef[]): void {
  for (const q of defs) QUESTS.set(q.id, q);
}

export function questDef(id: string): QuestDef {
  const q = QUESTS.get(id);
  if (!q) throw new Error(`Unknown quest '${id}'`);
  return q;
}

export function allQuests(): QuestDef[] {
  return [...QUESTS.values()];
}

export function isAvailable(c: CharacterSave, q: QuestDef): boolean {
  if (c.quests.done.includes(q.id) || c.quests.active[q.id]) return false;
  if (q.kind === 'daily' || q.kind === 'weekly') return false; // offered by the bounty board
  if ((q.requiresLevel ?? q.level - 2) > c.level) return false;
  if (q.requires?.some((r) => !c.quests.done.includes(r))) return false;
  if (q.requiresFlag && !c.flags[q.requiresFlag]) return false;
  return true;
}

export function availableFrom(c: CharacterSave, npcId: string): QuestDef[] {
  return allQuests().filter((q) => q.giver === npcId && isAvailable(c, q));
}

export function currentStep(q: QuestDef, p: QuestProgress): QuestStep | undefined {
  return q.steps[p.step];
}

export function isStepDone(q: QuestDef, p: QuestProgress): boolean {
  const st = currentStep(q, p);
  if (!st) return true;
  return st.objectives.every((o, i) => (p.counts[i] ?? 0) >= o.count);
}

export function isReadyToTurnIn(c: CharacterSave, id: string): boolean {
  const p = c.quests.active[id];
  if (!p) return false;
  const q = questDef(id);
  return p.step >= q.steps.length;
}

export function turnInsFor(c: CharacterSave, npcId: string): QuestDef[] {
  return Object.keys(c.quests.active)
    .map((id) => questDef(id))
    .filter((q) => q.turnIn === npcId && isReadyToTurnIn(c, q.id));
}

/** Most quests the HUD tracker shows at once. */
export const MAX_TRACKED = 7;

export function isTracked(c: CharacterSave, id: string): boolean {
  return c.quests.tracked.includes(id);
}

/**
 * Pins (or unpins) an active quest on the HUD tracker. Returns false when the
 * quest is not active or the tracker is already full.
 */
export function setTracked(c: CharacterSave, id: string, on: boolean): boolean {
  if (!on) {
    c.quests.tracked = c.quests.tracked.filter((t) => t !== id);
    return true;
  }
  if (!c.quests.active[id]) return false;
  if (c.quests.tracked.includes(id)) return true;
  if (c.quests.tracked.length >= MAX_TRACKED) return false;
  c.quests.tracked.push(id);
  return true;
}

/**
 * The next main-story quest to pick up: the first main quest (registration
 * order) that is neither done nor active and whose prerequisite quests are done.
 */
export function nextMainQuest(c: CharacterSave): QuestDef | null {
  for (const q of QUESTS.values()) {
    if (q.kind !== 'main' || c.quests.done.includes(q.id) || c.quests.active[q.id]) continue;
    if (q.requires?.some((r) => !c.quests.done.includes(r))) continue;
    return q;
  }
  return null;
}

export function accept(c: CharacterSave, id: string, now = Date.now()): boolean {
  const q = questDef(id);
  if (c.quests.active[id] || c.quests.done.includes(id)) return false;
  c.quests.active[id] = { id, step: 0, counts: q.steps[0]?.objectives.map(() => 0) ?? [], accepted: now };
  if (!c.quests.tracked.includes(id)) c.quests.tracked.unshift(id);
  c.quests.tracked = c.quests.tracked.slice(0, MAX_TRACKED);
  // Objectives already satisfied (e.g. level) complete immediately.
  progress(c, { type: 'level', level: c.level });
  return true;
}

export function abandon(c: CharacterSave, id: string): void {
  delete c.quests.active[id];
  c.quests.tracked = c.quests.tracked.filter((t) => t !== id);
}

function matches(o: QuestObjective, ev: QuestEvent): number {
  switch (ev.type) {
    case 'kill':
      return o.type === 'kill' && (o.target === ev.target || o.target === '*') ? 1 : 0;
    case 'talk':
      return o.type === 'talk' && o.target === ev.target ? 1 : 0;
    case 'reach':
      if (o.type !== 'reach' || !o.pos || o.zone !== ev.zone) return 0;
      return Math.hypot(ev.pos.x - o.pos.x, ev.pos.z - o.pos.z) <= (o.radius ?? 6) ? o.count : 0;
    case 'gather':
      return o.type === 'gather' && (o.target === ev.target || o.target === '*') ? (ev.count ?? 1) : 0;
    case 'interact':
      return o.type === 'interact' && o.target === ev.target ? 1 : 0;
    case 'clear':
      return o.type === 'clear' && o.target === ev.target ? 1 : 0;
    case 'collect':
      return o.type === 'collect' && o.target === ev.target ? (ev.count ?? 1) : 0;
    case 'level':
      return o.type === 'level' && ev.level >= Number(o.target) ? o.count : 0;
    case 'flag':
      return o.type === 'flag' && o.target === ev.target ? o.count : 0;
    case 'use':
      return o.type === 'use' && o.target === ev.target ? 1 : 0;
  }
}

export interface ProgressResult {
  updated: string[];
  stepsAdvanced: { quest: string; step: number; hint?: string }[];
  readyToTurnIn: string[];
}

/** Applies a game event to every active quest. */
export function progress(c: CharacterSave, ev: QuestEvent): ProgressResult {
  const res: ProgressResult = { updated: [], stepsAdvanced: [], readyToTurnIn: [] };
  for (const p of Object.values(c.quests.active)) {
    const q = QUESTS.get(p.id);
    if (!q) continue;
    let changed = false;
    // Loop so that consecutive steps satisfied by the same state (e.g. level) chain.
    for (let guard = 0; guard < 8; guard++) {
      const st = currentStep(q, p);
      if (!st) break;
      st.objectives.forEach((o, i) => {
        const cur = p.counts[i] ?? 0;
        if (cur >= o.count) return;
        const add = matches(o, ev);
        if (add > 0) {
          p.counts[i] = Math.min(o.count, cur + add);
          changed = true;
        }
      });
      if (!isStepDone(q, p)) break;
      p.step++;
      p.counts = q.steps[p.step]?.objectives.map(() => 0) ?? [];
      changed = true;
      if (p.step >= q.steps.length) {
        res.readyToTurnIn.push(q.id);
        break;
      }
      res.stepsAdvanced.push({ quest: q.id, step: p.step, hint: q.steps[p.step]?.hint });
      // A new step may already be satisfied by persistent state; only level/flag events re-check.
      if (ev.type !== 'level' && ev.type !== 'flag') break;
    }
    if (changed) res.updated.push(q.id);
  }
  return res;
}

/** Marks a quest complete (rewards are granted by the caller). */
export function complete(c: CharacterSave, id: string): QuestDef | null {
  if (!isReadyToTurnIn(c, id)) return null;
  const q = questDef(id);
  delete c.quests.active[id];
  c.quests.tracked = c.quests.tracked.filter((t) => t !== id);
  if (q.kind !== 'daily' && q.kind !== 'weekly') c.quests.done.push(id);
  for (const f of q.rewards.flags ?? []) c.flags[f] = true;
  return q;
}

/** Marker for an NPC's nameplate: '!' available, '?' ready to turn in. */
export function npcMarker(c: CharacterSave, npcId: string): '!' | '?' | '' {
  if (turnInsFor(c, npcId).length) return '?';
  // Talk objectives targeting this NPC.
  for (const p of Object.values(c.quests.active)) {
    const q = QUESTS.get(p.id);
    const st = q && currentStep(q, p);
    if (st?.objectives.some((o, i) => o.type === 'talk' && o.target === npcId && (p.counts[i] ?? 0) < o.count)) return '?';
  }
  return availableFrom(c, npcId).length ? '!' : '';
}

export interface TrackerLine {
  id: string;
  title: string;
  kind: QuestKind;
  objectives: { text: string; progress?: string; done?: boolean }[];
}

/**
 * HUD tracker entries: the quests the player tracks, story quests first, then
 * in tracking order. When no main quest is active, the next story step leads
 * the list ("Speak with …" or "Reach level N") so the main story is never lost.
 */
export function trackerLines(c: CharacterSave): TrackerLine[] {
  const out: TrackerLine[] = [];
  const mainActive = Object.keys(c.quests.active).some((id) => QUESTS.get(id)?.kind === 'main');
  const next = mainActive ? null : nextMainQuest(c);
  if (next) {
    const need = next.requiresLevel ?? next.level - 2;
    const text = need > c.level ? `Reach level ${need} to continue the story` : next.auto ? 'Continue the story' : `Speak with ${npcName(next.giver)}`;
    out.push({ id: next.id, title: next.name, kind: next.kind, objectives: [{ text, done: false }] });
  }
  // Story quests lead, the rest keep the order the player tracked them in.
  const rank = (id: string) => ({ main: 0, world: 1 })[QUESTS.get(id)?.kind as string] ?? 2;
  const ordered = c.quests.tracked.map((id, i) => ({ id, i })).sort((a, b) => rank(a.id) - rank(b.id) || a.i - b.i);
  for (const { id } of ordered) {
    if (out.length >= MAX_TRACKED) break;
    const p = c.quests.active[id];
    if (!p) continue;
    const q = QUESTS.get(id);
    if (!q) continue;
    const st = currentStep(q, p);
    if (!st) {
      out.push({ id, title: q.name, kind: q.kind, objectives: [{ text: `Report to ${npcName(q.turnIn)}`, done: false }] });
      continue;
    }
    out.push({
      id,
      title: q.name,
      kind: q.kind,
      objectives: st.objectives.map((o, i) => ({ text: o.text, progress: o.count > 1 ? `${p.counts[i] ?? 0}/${o.count}` : undefined, done: (p.counts[i] ?? 0) >= o.count })),
    });
  }
  return out;
}

/** Objective markers for the minimap in a zone. */
export function objectiveMarkers(c: CharacterSave, zone: string): V2[] {
  const out: V2[] = [];
  for (const p of Object.values(c.quests.active)) {
    const q = QUESTS.get(p.id);
    const st = q && currentStep(q, p);
    st?.objectives.forEach((o, i) => {
      if (o.zone === zone && o.pos && (p.counts[i] ?? 0) < o.count) out.push(o.pos);
    });
  }
  return out;
}

let npcNameFn: (id: string) => string = (id) => id;
export function setNpcNameResolver(fn: (id: string) => string): void {
  npcNameFn = fn;
}
function npcName(id: string): string {
  return npcNameFn(id);
}
